/* eslint-disable no-console */
import * as Sentry from '@sentry/vue';

/**
 * Central Sentry integration.
 *
 * All runtime Sentry access goes through this module: `initSentry` (called once
 * from main.js) plus the capture helpers. The only other Sentry touchpoint is
 * the build-time source-map upload in vite.config.mjs (`@sentry/vite-plugin`),
 * which is a separate build concern and cannot live here.
 *
 * Capture helpers use the imported SDK directly: its functions are always
 * defined and no-op until `initSentry` installs a client (which only happens in
 * prod/staging/dev), so they are safe to call from any environment.
 */

// Maps Sentry severity levels to the matching console method.
const CONSOLE_METHOD_BY_LEVEL = {
  fatal: 'error',
  error: 'error',
  warning: 'warn',
  log: 'log',
  info: 'info',
  debug: 'debug'
};

/**
 * Initialize the Sentry SDK across every Vite mode (including `localhost` and
 * `qa`) so developers can trigger and verify capture paths locally;
 * per-environment routing is handled by the `environment` tag.
 *
 * Must run before other app setup so the SDK installs its global handlers as
 * early as possible.
 *
 * @param {import('vue').App} app - The Vue app instance.
 * @param {import('vue-router').Router} router - The router, for tracing integration.
 */
export function initSentry(app, router) {
  if (!['production', 'staging', 'development', 'qa', 'localhost'].includes(import.meta.env.MODE)) {
    return;
  }

  const sentryClient = Sentry.init({
    app,
    dsn: 'https://df64a8a296038a32b7cbc62ac2f7c568@central.learning.asu.edu/6',
    environment: import.meta.env.VITE_ENV_HOOK || 'prod',
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration()
    ],
    tracesSampleRate: 0.1,
    profileSessionSampleRate: 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: true,
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
    beforeSend(event, hint) {
      if (hint?.originalException?.message === 'Network Error') {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          return null;
        }
        event.fingerprint = ['network-error'];
      }
      return event;
    }
  });

  // Defensive bind: if the global scope didn't pick up the client (an
  // intermittent v9 scope/binding issue we hit in staging), re-register it
  // so window.Sentry.captureException et al. find the client.
  if (sentryClient && Sentry.getClient() !== sentryClient) {
    Sentry.setCurrentClient(sentryClient);
  }

  // ASU's tag managers (GTM/Tealium/HubSpot) inject an older Sentry SDK at
  // runtime which hijacks window.Sentry without a client. Override it so
  // console-triggered captures and any external code reading window.Sentry
  // use the same instance that called init.
  window.Sentry = Sentry;
}

/**
 * Capture a message to Sentry, optionally logging to console.
 *
 * @param {string} message - The message to capture.
 * @param {Object} [options]
 * @param {('fatal'|'error'|'warning'|'log'|'info'|'debug')} [options.level='info'] - Severity level.
 * @param {boolean} [options.logToConsole=false] - Also log to the console at the matching level.
 * @param {Object} [options.context={}] - Additional Sentry capture context (e.g. `tags`, `extra`).
 */
export function captureMessage(message, { level = 'info', logToConsole = false, context = {} } = {}) {
  if (logToConsole) {
    const consoleMethod = CONSOLE_METHOD_BY_LEVEL[level] || 'log';
    console[consoleMethod](message);
  }

  Sentry.captureMessage(message, { level, ...context });
}

/**
 * Capture an exception to Sentry, optionally logging to console.
 *
 * @param {Error|*} error - The error to capture.
 * @param {Object} [options]
 * @param {boolean} [options.logToConsole=false] - Also log the error to the console.
 * @param {Object} [options.context={}] - Additional Sentry capture context (e.g. `tags`, `extra`).
 */
export function captureException(error, { logToConsole = false, context = {} } = {}) {
  if (logToConsole) {
    console.error(error);
  }

  Sentry.captureException(error, context);
}

/**
 * Associate subsequent events with a user (internal id only — no PII).
 *
 * @param {Object|null} user - User identity, or null to clear.
 */
export function setUser(user) {
  Sentry.setUser(user);
}
